<script lang="ts">
const DRAG_THRESHOLD = 5

import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToFix'
});</script>
<template>
	<div class="to-fix" :class="setClass">
		<div ref="content" class="to-fix-wrapper" :style="setStyle" @mousedown="onMouseDown" @touchstart="onTouchStart">
			<slot></slot>
			<to-tag v-if="Number(number)" class="to-fix-number" type="number">{{ number }}</to-tag>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onActivated, onDeactivated, onBeforeUnmount, nextTick } from 'vue'
import { getEm } from '@touchvue/utils'

// 定义 props
const props = defineProps({
	mode: {
		type: String,
		default: 'default'
	},
	top: {
		type: [String, Number],
		default: 'auto'
	},
	number: {
		type: [String, Number],
		default: ''
	},
	left: {
		type: [String, Number],
		default: 'auto'
	},
	right: {
		type: [String, Number],
		default: 1
	},
	bottom: {
		type: [String, Number],
		default: 1
	},
	appendToBody: {
		type: Boolean,
		default: true
	},
	draggable: Boolean
})

// 响应式数据
const content = ref<HTMLElement | null>(null)
const resizeTimer = ref<number | null>(null)
const isDown = ref(false)
const offsetX = ref(0)
const offsetY = ref(0)
const currentTop = ref<string | null>(null)
const currentLeft = ref<string | null>(null)
const currentRight = ref<string | null>(null)
const currentBottom = ref<string | null>(null)
const isDraging = ref(false) // 拖动开始标志
const startX = ref(0) // 记录起始X位置
const startY = ref(0) // 记录起始Y位置
 // 拖动阈值，5px

// 计算属性
const setStyle = computed(() => {
	const obj: Record<string, string> = {}
	if (currentTop.value) {
		obj.top = currentTop.value
		obj.bottom = 'auto'
	} else {
		if (props.top && props.top !== 'auto') {
			obj.top = props.top + 'em'
			obj.bottom = 'auto'
		} else if (props.bottom && props.bottom !== 'auto') {
			obj.bottom = props.bottom + 'em'
			obj.top = 'auto'
		}
	}
	if (currentLeft.value) {
		obj.left = currentLeft.value
		obj.right = 'auto'
	} else {
		if (props.left && props.left !== 'auto') {
			obj.left = props.left + 'em'
			obj.right = 'auto'
		} else if (props.right && props.right !== 'auto') {
			obj.right = props.right + 'em'
			obj.left = 'auto'
		}
	}
	return obj
})

const setClass = computed(() => {
	const arr = []
	if (props.mode) {
		arr.push(`mode-${props.mode}`)
	}
	if (isDraging.value) {
		arr.push(`is-dragging`)
	}
	return arr
})

// 监听器
watch(isDown, val => {
	if (val) {
		document.body.style.userSelect = 'none'
	} else {
		document.body.style.userSelect = ''
	}
})

// 方法
const init = () => {
	if (props.appendToBody && content.value?.parentElement) {
		document.body.appendChild(content.value.parentElement)
	}
	window.addEventListener('resize', resize)
}

const destroy = () => {
	window.removeEventListener('resize', resize)
	if (props.appendToBody && content.value?.parentElement?.parentNode) {
		content.value.parentElement.parentNode.removeChild(content.value.parentElement)
	}
}

// 鼠标事件
const onMouseDown = (event: MouseEvent) => {
	if (!props.draggable) return

	isDown.value = true
	isDraging.value = false // 重置拖动开始标志
	startX.value = event.clientX // 记录起始位置
	startY.value = event.clientY
	if (content.value) {
		const rect = content.value.getBoundingClientRect()
		offsetX.value = event.clientX - rect.left
		offsetY.value = event.clientY - rect.top
	}
	document.addEventListener('mousemove', onMouseMove)
	document.addEventListener('mouseup', onMouseUp)
}

const onMouseMove = (event: MouseEvent) => {
	if (!isDown.value) return

	// 计算移动距离
	const distance = Math.sqrt(Math.pow(event.clientX - startX.value, 2) + Math.pow(event.clientY - startY.value, 2))

	// 只有当移动距离超过阈值时才开始拖动
	if (distance >= DRAG_THRESHOLD) {
		isDraging.value = true // 标记拖动已经开始
		setPosition(event.clientX, event.clientY)
	}
}

const onMouseUp = () => {
	isDown.value = false
	isDraging.value = false
	document.removeEventListener('mousemove', onMouseMove)
	document.removeEventListener('mouseup', onMouseUp)
	setEmUnit()
}

// 触摸事件
const onTouchStart = (event: TouchEvent) => {
	if (!props.draggable) return

	isDown.value = true
	isDraging.value = false // 重置拖动开始标志

	const touch = event.touches[0]
	startX.value = touch.clientX // 记录起始位置
	startY.value = touch.clientY
	if (content.value) {
		const rect = content.value.getBoundingClientRect()
		offsetX.value = touch.clientX - rect.left
		offsetY.value = touch.clientY - rect.top
	}

	document.addEventListener('touchmove', onTouchMove)
	document.addEventListener('touchend', onTouchEnd)
}

const onTouchMove = (event: TouchEvent) => {
	if (!isDown.value) return

	const touch = event.touches[0]
	// 计算移动距离
	const distance = Math.sqrt(Math.pow(touch.clientX - startX.value, 2) + Math.pow(touch.clientY - startY.value, 2))

	// 只有当移动距离超过阈值时才开始拖动
	if (distance >= DRAG_THRESHOLD) {
		isDraging.value = true // 标记拖动已经开始
		setPosition(touch.clientX, touch.clientY)
	}
}

const onTouchEnd = () => {
	isDown.value = false
	isDraging.value = false
	document.removeEventListener('touchmove', onTouchMove)
	document.removeEventListener('touchend', onTouchEnd)
	setEmUnit()
}

const setEmUnit = () => {
	if (content.value) {
		currentTop.value = content.value.getBoundingClientRect().top / getEm() + 'em'
		currentLeft.value = content.value.getBoundingClientRect().left / getEm() + 'em'
	}
}

const setPosition = (clientX?: number, clientY?: number) => {
	if (!content.value) return

	const container = content.value.offsetParent || document.documentElement
	const containerRect = container.getBoundingClientRect()
	const elRect = content.value.getBoundingClientRect()
	const elWidth = content.value.offsetWidth
	const elHeight = content.value.offsetHeight
	let newLeft = clientX ? clientX - offsetX.value : elRect.left
	let newTop = clientY ? clientY - offsetY.value : elRect.top

	// 确保元素不会拖出可视范围
	if (newLeft < 0) newLeft = 0
	if (newLeft + elWidth > containerRect.width) newLeft = containerRect.width - elWidth
	if (newTop < 0) newTop = 0
	if (newTop + elHeight > containerRect.height) newTop = containerRect.height - elHeight

	currentTop.value = `${newTop}px`
	currentLeft.value = `${newLeft}px`
	currentRight.value = 'auto'
	currentBottom.value = 'auto'
}

const resize = () => {
	if (resizeTimer.value) {
		clearTimeout(resizeTimer.value)
	}
	resizeTimer.value = window.setTimeout(() => {
		setPosition()
		nextTick(() => {
			setEmUnit()
		})
	}, 300)
}

// 生命周期钩子
onMounted(() => {
	init()
})

onActivated(() => {
	init()
})

onDeactivated(() => {
	destroy()
})

onBeforeUnmount(() => {
	destroy()
})
</script>
